import React, { useState, useMemo } from 'react';
import { Button, DatePicker, Modal, Popover, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import dayjs, { Dayjs } from 'dayjs';
import changeLogApi, { ChangeLogEntityType } from './changeLogApi';
import './changeLogHistoryModal.css';

const { Text } = Typography;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChangeLogEntry {
    dt: string;
    user: string;
    field: string;
    ov?: string;
    nv?: string;
}

export interface ChangeLogHistoryModalProps {
    entityType: ChangeLogEntityType;
    entityId: number | string;
    buttonLabel?: string;
    fieldLabelMap?: Record<string, string>;
    excludeFields?: string[];  
    fetchChangeLogs?: (
        entityType: ChangeLogEntityType,
        entityId: number | string,
        startDate: string,
        endDate: string
    ) => Promise<ChangeLogEntry[]>;
}

// ─── Pagination helpers ───────────────────────────────────────────────────────

const PAGE_SIZE = 10;

function buildPageNums(current: number, total: number): (number | '…')[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | '…')[] = [1];
    if (current > 3) pages.push('…');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
        pages.push(i);
    }
    if (current < total - 2) pages.push('…');
    pages.push(total);
    return pages;
}

// ─── Custom Pagination Footer ─────────────────────────────────────────────────

interface PaginationFooterProps {
    current: number;
    total: number;
    onChange: (page: number) => void;
}

const PaginationFooter: React.FC<PaginationFooterProps> = ({ current, total, onChange }) => {
    const totalPages = Math.ceil(total / PAGE_SIZE);
    const start      = (current - 1) * PAGE_SIZE + 1;
    const end        = Math.min(current * PAGE_SIZE, total);

    const btnBase: React.CSSProperties = {
        minWidth: 32, height: 32, padding: '0 10px',
        border: '1px solid #d9d9d9', borderRadius: 4,
        background: '#fff', color: 'rgba(0,0,0,0.65)',
        fontSize: 14, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.2s', userSelect: 'none' as const,
    };
    const btnActive:   React.CSSProperties = { ...btnBase, color: '#1890ff', borderColor: '#1890ff', fontWeight: 600, background: '#e6f7ff' };
    const btnDisabled: React.CSSProperties = { ...btnBase, color: 'rgba(0,0,0,0.25)', cursor: 'not-allowed', background: '#f5f5f5' };

    return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 24px', borderTop: '1px solid #f0f0f0', background: '#fff',
        }}>
            <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.45)' }}>
                Showing {start}–{end} of {total} change{total !== 1 ? 's' : ''}
            </span>

            {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button
                        style={current === 1 ? btnDisabled : btnBase}
                        disabled={current === 1}
                        onClick={() => current > 1 && onChange(current - 1)}
                    >
                        « Prev
                    </button>

                    {buildPageNums(current, totalPages).map((n, idx) =>
                        n === '…' ? (
                            <span key={`ellipsis-${idx}`} style={{ padding: '0 4px', color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>...</span>
                        ) : (
                            <button
                                key={n}
                                style={n === current ? btnActive : btnBase}
                                onClick={() => onChange(n as number)}
                            >
                                {n}
                            </button>
                        )
                    )}

                    <button
                        style={current === totalPages ? btnDisabled : btnBase}
                        disabled={current === totalPages}
                        onClick={() => current < totalPages && onChange(current + 1)}
                    >
                        Next »
                    </button>
                </div>
            )}
        </div>
    );
};

function parseDealTypeConfig(raw: string): Record<string, string | null> {
    const inner = raw.match(/\((.+)\)/)?.[1] ?? '';
    return Object.fromEntries(
        inner.split(',').map((pair) => {
            const [key, val] = pair.trim().split('=');
            return [key.trim(), val?.trim() === 'null' ? null : val?.trim() ?? null];
        })
    );
}

export function expandDealTypeConfiguration(entries: ChangeLogEntry[]): ChangeLogEntry[] {
    const result: ChangeLogEntry[] = [];

    for (const entry of entries) {
        if (entry.field !== 'dealTypeConfiguration') {
            result.push(entry);
            continue;
        }

        const ovMap = entry.ov ? parseDealTypeConfig(entry.ov) : {};
        const nvMap = entry.nv ? parseDealTypeConfig(entry.nv) : {};

        // Union of all keys from both old and new
        const allKeys = new Set([...Object.keys(ovMap), ...Object.keys(nvMap)]);

        for (const key of allKeys) {
            const ov = ovMap[key] ?? null;
            const nv = nvMap[key] ?? null;

            // Skip if both sides are null — field wasn't involved in this change
            if (ov === null && nv === null) continue;

            // Skip if old and new are identical — no actual change
            if (ov === nv) continue;

            result.push({
                dt:    entry.dt,
                user:  entry.user,
                field: key,                  // e.g. 'fixedCpm' — resolved via label map below
                ov:    ov ?? undefined,
                nv:    nv ?? undefined,
            });
        }
    }

    return result;
}

function parseClusterEndpoints(raw: string): Record<string, string | null> {
    return Object.fromEntries(
        raw.split(',').map((pair) => {
            const idx = pair.indexOf('=');
            if (idx === -1) return [pair.trim(), null];
            const key = pair.slice(0, idx).trim();
            const val = pair.slice(idx + 1).trim();
            return [key, val === '' || val === 'null' ? null : val];
        })
    );
}

export function expandClusterEndpoints(entries: ChangeLogEntry[]): ChangeLogEntry[] {
    const result: ChangeLogEntry[] = [];

    for (const entry of entries) {
        if (entry.field !== 'clusterEndpoints') {
            result.push(entry);
            continue;
        }

        const ovMap = entry.ov ? parseClusterEndpoints(entry.ov) : {};
        const nvMap = entry.nv ? parseClusterEndpoints(entry.nv) : {};

        const allKeys = new Set([...Object.keys(ovMap), ...Object.keys(nvMap)]);

        for (const key of allKeys) {
            const ov = ovMap[key] ?? null;
            const nv = nvMap[key] ?? null;

            if (ov === null && nv === null) continue;
            if (ov === nv) continue;

            result.push({
                dt:    entry.dt,
                user:  entry.user,
                field: key,
                ov:    ov ?? undefined,
                nv:    nv ?? undefined,
            });
        }
    }

    return result;
}



// ─── Value cell: red pill = old, green pill = new ─────────────────────────────

const ValueCell: React.FC<{ value?: string; variant: 'old' | 'new' }> = ({ value, variant }) => {
    if (!value) return <Text type="secondary">—</Text>;
    return (
        <Popover
            content={
                <div style={{ maxWidth: 320, maxHeight: 400, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: 13, color: '#fff' }}>
                    {value}
                </div>
            }
            trigger="hover"
            placement="topLeft"
            overlayClassName="changelog-value-popover"
            overlayInnerStyle={{ background: 'rgba(0, 0, 0, 0.85)', padding: '8px 12px' }}
            arrow={{ pointAtCenter: true }}
        >
            <Tag
                color={variant === 'old' ? 'error' : 'success'}
                style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', cursor: 'pointer' }}
            >
                {value}
            </Tag>
        </Popover>
    );
};

// ─── Calendar SVG icon (matches prototype screenshot) ────────────────────────

const CalendarIcon = () => (
    <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="5" y="9" width="42" height="37" rx="3" stroke="#d0d0d0" strokeWidth="2" fill="none" />
        <rect x="5" y="9" width="42" height="12" rx="3" fill="none" stroke="#d0d0d0" strokeWidth="2" />
        <line x1="15" y1="5" x2="15" y2="15" stroke="#d0d0d0" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="37" y1="5" x2="37" y2="15" stroke="#d0d0d0" strokeWidth="2.5" strokeLinecap="round" />
        <text x="26" y="37" textAnchor="middle" fontSize="14" fontWeight="700" fill="#c0c0c0"
            fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif">1</text>
    </svg>
);

// ─── Empty / no-results state ─────────────────────────────────────────────────

const EmptyState: React.FC<{ searched: boolean }> = ({ searched }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px 72px', gap: 6 }}>
        {searched ? (
            <>
                <span style={{ fontSize: 44, lineHeight: 1, marginBottom: 6 }}>🔍</span>
                <Text style={{ fontSize: 14, color: 'rgba(0,0,0,0.65)' }}>No changes found for this date range</Text>
                <Text style={{ fontSize: 13, color: 'rgba(0,0,0,0.35)' }}>Try selecting a different date range.</Text>
            </>
        ) : (
            <>
                <CalendarIcon />
                <div style={{ marginTop: 10, textAlign: 'center' }}>
                    <Text style={{ fontSize: 14, color: 'rgba(0,0,0,0.65)' }}>
                        Select a date range and click <strong>Show Changes</strong>
                    </Text>
                </div>
                <Text style={{ fontSize: 13, color: 'rgba(0,0,0,0.35)', textAlign: 'center' }}>
                    All field modifications, status changes and configuration changes will appear here
                </Text>
            </>
        )}
    </div>
);

// ─── Date field label (all-caps, matches screenshot) ─────────────────────────

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(0,0,0,0.45)', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
        {children}
    </span>
);

// ─── CSV export ───────────────────────────────────────────────────────────────

function exportToCSV(data: ChangeLogEntry[], fieldLabelMap?: Record<string, string>) {
    const getLabel = (field: string) => fieldLabelMap?.[field] ?? field;
    const header = ['Date & Time', 'User', 'Field', 'Old Value', 'New Value'];
    const esc    = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const rows   = data.map((r) => [
        `"\t${r.dt}"`,   // ← only change: \t prefix forces plain text in all apps
        esc(r.user),
        esc(getLabel(r.field)),
        esc(r.ov ?? ''),
        esc(r.nv ?? ''),
    ]);
    const csv    = [header.map(esc), ...rows].map((row) => row.join(',')).join('\n');
    const a = document.createElement('a');
    a.href  = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'change_log_history.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

// ─── Main component ───────────────────────────────────────────────────────────

const ChangeLogHistoryModal: React.FC<ChangeLogHistoryModalProps> = ({
    entityType,
    entityId,
    buttonLabel = 'Change Log History',
    fieldLabelMap,
    excludeFields = [],
    fetchChangeLogs,
}) => {
    const getLabel = (field: string) => fieldLabelMap?.[field] ?? field;
    const [open, setOpen]             = useState(false);
    const [loading, setLoading]       = useState(false);
    const [startDate, setStartDate]   = useState<Dayjs | null>(null);
    const [endDate, setEndDate]       = useState<Dayjs | null>(null);
    const [data, setData]             = useState<ChangeLogEntry[]>([]);
    const [searched, setSearched]     = useState(false);
    const [hasResults, setHasResults] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState<React.Key | null>('dt');
    const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | null>('descend');

    const getSorterTooltip = (colKey: string) => {
        if (sortField !== colKey || !sortOrder) return 'Click to sort ascending';
        if (sortOrder === 'ascend') return 'Click to sort descending';
        return 'Click to cancel sorting';
    };

    // Slice data for current page
    const pagedData = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return data.slice(start, start + PAGE_SIZE);
    }, [data, currentPage]);

    const handleOpen = () => {
        setOpen(true);
        setData([]);
        setSearched(false);
        setHasResults(false);
        setStartDate(null);
        setEndDate(null);
        setCurrentPage(1);
        setSortField('dt');
        setSortOrder('descend');
    };

    const handleClose = () => setOpen(false);

    const excludeSet = useMemo(() => new Set(excludeFields), [excludeFields]);

    const handleShowChanges = async () => {
        if (!startDate || !endDate) return;
    
        const start = startDate.format('YYYY-MM-DD');
        const end   = endDate.format('YYYY-MM-DD');
    
        setLoading(true);
        setSearched(false);
        setData([]);
        setHasResults(false);
    
        try {
            let result: ChangeLogEntry[];
    
            if (fetchChangeLogs) {
                result = await fetchChangeLogs(entityType, entityId, start, end);
            } else {
                result = await changeLogApi.fetchChangeLogs(entityType, start, end, entityId);
            }
    
            // Guard: ensure we always have an array
            const safeResult = Array.isArray(result) ? result : [];
    
            const expanded = expandClusterEndpoints(expandDealTypeConfiguration(safeResult));

            // Filter out excluded fields before storing in state
            const filtered = excludeSet.size
                ? expanded.filter((entry) => !excludeSet.has(entry.field))
                : expanded;
    
            setData(filtered);
            setHasResults(filtered.length > 0);
            setCurrentPage(1);
        } catch (err) {
            console.error('[ChangeLogHistoryModal] Failed to fetch change logs:', err);
            setData([]);
            setHasResults(false);
        } finally {
            setLoading(false);
            setSearched(true);
        }
    };

    // ─── Table columns ────────────────────────────────────────────────────────

    const columns: ColumnsType<ChangeLogEntry> = [
        {
            title: <span className="sorter-header-label" data-tooltip={getSorterTooltip('dt')}>Date &amp; Time</span>,
            dataIndex: 'dt',
            key: 'dt',
            sorter: (a, b) => a.dt.localeCompare(b.dt),
            defaultSortOrder: 'descend',
            showSorterTooltip: false,
            width: 155,
            render: (v: string) => <Text style={{ whiteSpace: 'nowrap' }}>{v}</Text>,
        },
        {
            title: <span className="sorter-header-label" data-tooltip={getSorterTooltip('user')}>User</span>,
            dataIndex: 'user',
            key: 'user',
            sorter: (a, b) => a.user.localeCompare(b.user),
            showSorterTooltip: false,
            width: 210,
            ellipsis: { showTitle: false },
            render: (v: string) => <Tooltip title={v}><Text>{v}</Text></Tooltip>,
        },
        {
            title: <span className="sorter-header-label" data-tooltip={getSorterTooltip('field')}>Field</span>,
            dataIndex: 'field',
            key: 'field',
            sorter: (a, b) => a.field.localeCompare(b.field),
            showSorterTooltip: false,
            width: 170,
            render: (v: string) => <Text strong>{getLabel(v)}</Text>,
        },
        {
            title: 'Old Value',
            dataIndex: 'ov',
            key: 'ov',
            width: 190,
            render: (v?: string) => <ValueCell value={v} variant="old" />,
        },
        {
            title: 'New Value',
            dataIndex: 'nv',
            key: 'nv',
            width: 190,
            render: (v?: string) => <ValueCell value={v} variant="new" />,
        },
    ];

    return (
        <>
            {/* ── Trigger button ── */}
            <Button onClick={handleOpen}>
                {buttonLabel}
            </Button>

            {/* ── Modal ── */}
            <Modal
                title="Change Log History"
                open={open}
                onCancel={handleClose}
                footer={null}
                width={1200}
                style={{ top: 20 }}
                destroyOnClose
                styles={{
                    header: { padding: '16px 20px 14px', borderBottom: '1px solid #f0f0f0', marginBottom: 0, fontSize: 16, fontWeight: 600 },
                    body: { padding: 0 },
                }}
            >
                {/* ── Filter bar ── */}
                <div style={{
                    display: 'flex', alignItems: 'flex-end', gap: 16,
                    padding: '14px 24px 16px', borderBottom: '1px solid #f0f0f0',
                    background: '#f5f5f5', flexWrap: 'wrap',
                    position: 'relative', zIndex: 1,
                }}>
                    {/* START DATE */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <FieldLabel>Start Date</FieldLabel>
                        <DatePicker
                            value={startDate}
                            onChange={(v) => setStartDate(v)}
                            format="MM/DD/YYYY"
                            placeholder="mm/dd/yyyy"
                            allowClear
                            disabledDate={(d) => {
                                if (endDate && d > endDate.endOf('day')) return true;
                                return d > dayjs().endOf('day');
                            }}
                            style={{ width: 160, height: 32 }}
                        />
                    </div>

                    {/* END DATE */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <FieldLabel>End Date</FieldLabel>
                        <DatePicker
                            value={endDate}
                            onChange={(v) => setEndDate(v)}
                            format="MM/DD/YYYY"
                            placeholder="mm/dd/yyyy"
                            allowClear
                            disabledDate={(d) => {
                                if (startDate && d < startDate.startOf('day')) return true;
                                return d > dayjs().endOf('day');
                            }}
                            style={{ width: 160, height: 32 }}
                        />
                    </div>

                    {/* Show Changes */}
                    <Button
                        type="primary"
                        onClick={handleShowChanges}
                        disabled={!startDate || !endDate}
                        loading={loading}
                        style={{ height: 32, alignSelf: 'flex-end' }}
                    >
                        Show Changes
                    </Button>

                    {/* Export CSV — only after results load */}
                    {hasResults && (
                        <Button                            
                            onClick={() => exportToCSV(data, fieldLabelMap)}
                            style={{ height: 32, alignSelf: 'flex-end' }}
                        >
                            Export to CSV
                        </Button>
                    )}
                </div>

                {/* ── Body: empty state OR table + footer ── */}
                {!searched || !hasResults ? (
                    <EmptyState searched={searched} />
                ) : (
                    <div style={{ position: 'relative', zIndex: 2 }}>
                        <Table
                            className="changelog-table"
                            columns={columns}
                            dataSource={pagedData.map((r, i) => ({ ...r, key: i }))}
                            loading={loading}
                            pagination={false}
                            size="middle"
                            scroll={{ x: 900 }}
                            onChange={(_, __, sorter) => {
                                const s = Array.isArray(sorter) ? sorter[0] : (sorter as SorterResult<ChangeLogEntry>);
                                setSortField(Array.isArray(s.field) ? s.field[0] : (s.field ?? null));
                                setSortOrder((s.order as 'ascend' | 'descend' | null) ?? null);
                            }}
                        />
                        <PaginationFooter
                            current={currentPage}
                            total={data.length}
                            onChange={(p) => setCurrentPage(p)}
                        />
                    </div>
                )}
            </Modal>
        </>
    );
};

export default ChangeLogHistoryModal;